import { consentModeSnippet } from './consent';

/**
 * Google Tag Manager rendered into the document, for server-rendered apps.
 *
 * The SPA calls `initGtm()` from its entry, which injects the container from
 * JavaScript once the bundle has parsed and run. A server-rendered page would
 * push the tag behind hydration that way and lose the start of the visit — the
 * window that matters for bounce and first-paint measurement — so the official
 * snippet is rendered as markup instead and starts loading with the page.
 *
 * Consent Mode defaults go first, in the same inline script: `initGtm()` runs
 * `initConsentMode()` before the container for the same reason, and without it
 * GDPR-country visitors would be tracked before answering the cookie banner.
 */

/** Same default as `initGtm`. */
const DEFAULT_GTM_ENVIRONMENTS = ['develop', 'testing', 'production'];

export interface GtmSnippetProps {
  /** GTM container ID, e.g. 'GTM-XXXXXXX'. */
  gtmId: string;
  /** Current app environment, e.g. `import.meta.env.VITE_APP_TRIPLABO_ENV`. */
  environment: string;
  /** Environments in which GTM should load. Defaults to develop/testing/production. */
  environments?: readonly string[];
}

const loader = (gtmId: string) =>
  `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':` +
  `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],` +
  `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=` +
  `'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);` +
  `})(window,document,'script','dataLayer',${JSON.stringify(gtmId)});`;

/**
 * Consent defaults plus the GTM loader, for `<head>`. Renders nothing outside
 * the allowed environments, so local development does not reach production
 * analytics.
 */
export function GtmScript({
  gtmId,
  environment,
  environments = DEFAULT_GTM_ENVIRONMENTS
}: GtmSnippetProps) {
  if (!environments.includes(environment)) {
    return null;
  }
  // Fixed strings around build-time constants (the container ID, the GDPR
  // country list); no user input reaches this.
  return (
    <script
      dangerouslySetInnerHTML={{ __html: consentModeSnippet() + loader(gtmId) }}
    />
  );
}

/**
 * The GTM `<noscript>` iframe, which must be the first thing inside `<body>`.
 * Separate from `GtmScript` because the two belong in different parts of the
 * document.
 */
export function GtmNoScript({
  gtmId,
  environment,
  environments = DEFAULT_GTM_ENVIRONMENTS
}: GtmSnippetProps) {
  if (!environments.includes(environment)) {
    return null;
  }
  return (
    <noscript>
      {/* Titled even though hidden: an untitled iframe is an accessibility
          failure regardless of visibility, and GTM's own snippet carries the
          same title. */}
      <iframe
        title="Google Tag Manager"
        src={`https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      />
    </noscript>
  );
}
